import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, Grid, Button, TextField, MenuItem,
  Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Alert, Autocomplete, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, InputAdornment,
  useTheme, useMediaQuery, FormControlLabel, Checkbox
} from '@mui/material';
import { Add, Delete, Edit, Reply, Visibility, AttachFile, Close, Download, BlockOutlined, FilterList, Search, Clear, GppMaybe, Schedule, ReportProblemOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { activitiesAPI, categoriesAPI, projectsAPI, filesAPI, holidaysAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FileUpload from '../../components/common/FileUpload';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SlideTransition from '../../components/common/SlideTransition';
import DevBadge from '../../components/common/DevBadge';

dayjs.locale('es');

const emptyForm = {
  projectId: '', date: dayjs(), startTime: '', endTime: '',
  description: '', category: '', milestoneId: '', relatedPeople: [], evidences: [], overtimeEvidences: []
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ActivityLog() {
  const { user } = useAuth();
  const isTeamLead = user?.role === 'teamlead' || user?.role === 'clientlead';
  const canDispute = user?.role === 'teamlead';
  const [searchParams] = useSearchParams();
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Autocomplete states
  const [descOptions, setDescOptions] = useState([]);
  const [descLoading, setDescLoading] = useState(false);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [peopleInput, setPeopleInput] = useState('');

  const [confirm, setConfirm] = useState({ open: false, id: null });
  const [milestones, setMilestones] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [previewFile, setPreviewFile] = useState({ open: false, signedUrl: '', fileName: '', type: '' });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [descAppend, setDescAppend] = useState('');
  const [disputeDialog, setDisputeDialog] = useState({ open: false, actId: null });
  const [disputeMsg, setDisputeMsg] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dailyHoursLogged, setDailyHoursLogged] = useState(0);
  const [dailyHoursLoading, setDailyHoursLoading] = useState(false);
  const [fullDay, setFullDay] = useState(true);
  const [highlightId, setHighlightId] = useState(null);
  const highlightTimer = useRef(null);
  const [holidays, setHolidays] = useState([]);

  const triggerHighlight = useCallback((id) => {
    if (!id) return;
    clearTimeout(highlightTimer.current);
    setHighlightId(id);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 3500);
  }, []);

  // Filters — UI display states
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterProject, setFilterProject] = useState(() => searchParams.get('projectId') || '');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDesc, setFilterDesc] = useState('');
  const [filterOvertime, setFilterOvertime] = useState(false);
  // Single apiFilters object drives the actual API call
  const initPid = searchParams.get('projectId') || '';
  const [apiFilters, setApiFilters] = useState(() => initPid ? { projectId: initPid } : {});
  const descDebounce = useRef(null);
  const descInputDebounce = useRef(null);
  const peopleDebounce = useRef(null);

  // Sync when URL params change (e.g. user navigates to same route with different ?projectId)
  useEffect(() => {
    const pid = searchParams.get('projectId') || '';
    if (pid) {
      setFilterProject(pid);
      setApiFilters({ projectId: pid });
      setPage(1);
    }
  }, [searchParams]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const loadData = useCallback(async (p = 1, filters = {}) => {
    try {
      const params = { page: p, limit: 20, ...filters };
      const [acts, prjs, cats] = await Promise.all([
        activitiesAPI.getAll(params),
        projectsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setActivities(acts.data?.logs || acts.data || []);
      setTotal(acts.data?.total || 0);
      setProjects(prjs.data);
      setCategories(cats.data);
    } catch { } finally { setLoading(false); }
  }, []);

  // Load milestones when project selection changes in the dialog
  useEffect(() => {
    if (!openDialog || !form.projectId) { setMilestones([]); return; }
    projectsAPI.getById(form.projectId)
      .then(res => setMilestones((res.data?.milestones || []).filter(m => m.status === 'active')))
      .catch(() => setMilestones([]));
  }, [openDialog, form.projectId]);

  useEffect(() => { loadData(1, apiFilters); }, [apiFilters, loadData]);

  // Derived: project object matching the current form selection
  const selectedProject = useMemo(
    () => projects.find(p => p._id === form.projectId) || null,
    [projects, form.projectId]
  );

  // Compute hours from time range
  const computedHours = useMemo(() => {
    if (!form.startTime || !form.endTime) return 0;
    const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const diff = toMin(form.endTime) - toMin(form.startTime);
    return diff > 0 ? Math.round(diff / 15) / 4 : 0;
  }, [form.startTime, form.endTime]);

  const dailyLimit = selectedProject?.workSchedule?.dailyHoursLimit || 8;
  const dateStr = form.date?.isValid?.() ? form.date.format('YYYY-MM-DD') : null;

  // Holiday for the currently selected date (null if none)
  const isHolidayDetected = useMemo(() => {
    if (!dateStr || holidays.length === 0) return null;
    return holidays.find(h => (h.date || '').slice(0, 10) === dateStr) || null;
  }, [dateStr, holidays]);

  const isOvertimeDetected = useMemo(() => {
    if (editItem?.dispute?.status === 'pending') return false;
    if (!form.projectId || !form.date?.isValid?.()) return false;
    // Holiday → all hours are overtime regardless of time range
    if (isHolidayDetected) return true;
    const ws = selectedProject?.workSchedule;
    const scheduleEndTime = ws?.endTime || '18:00';
    const workDays = ws?.workDays?.length ? ws.workDays : [1, 2, 3, 4, 5];
    // Check non-working day (ISO: 1=Mon...7=Sun)
    const jsDay = form.date.toDate().getDay();
    const isoDay = jsDay === 0 ? 7 : jsDay;
    if (!workDays.includes(isoDay)) return true;
    // Full day always submits 08:00–18:00 blocks, never exceeds schedule end
    if (!editItem && fullDay) return false;
    if (!form.endTime) return false;
    const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    return toMin(form.endTime) > toMin(scheduleEndTime);
  }, [form.projectId, form.date, form.endTime, selectedProject, editItem, fullDay, isHolidayDetected]);

  // Load holidays for the project's client when the dialog is open
  useEffect(() => {
    if (!openDialog || !form.projectId || !selectedProject) { setHolidays([]); return; }
    const clientId = selectedProject.clientId?._id || selectedProject.clientId;
    if (!clientId) { setHolidays([]); return; }
    const year = form.date?.isValid?.() ? form.date.year() : dayjs().year();
    holidaysAPI.getAll({ clientId, year })
      .then(res => setHolidays(res.data || []))
      .catch(() => setHolidays([]));
  }, [openDialog, form.projectId, selectedProject, form.date?.year()]);

  // Fetch daily hours already logged for this user+project+date when dialog opens
  useEffect(() => {
    if (!openDialog || !form.projectId || !dateStr || editItem?.dispute?.status === 'pending') {
      setDailyHoursLogged(0);
      return undefined;
    }
    let cancelled = false;
    setDailyHoursLoading(true);
    activitiesAPI.getDailySummary({ projectId: form.projectId, date: dateStr, excludeId: editItem?._id || undefined })
      .then(res => { if (!cancelled) setDailyHoursLogged(res.data?.totalHours || 0); })
      .catch(() => { if (!cancelled) setDailyHoursLogged(0); })
      .finally(() => { if (!cancelled) setDailyHoursLoading(false); });
    return () => { cancelled = true; };
  }, [openDialog, form.projectId, dateStr, editItem]);

  const updateApiFilter = useCallback((key, value) => {
    setPage(1);
    setApiFilters(prev => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }, []);

  const handleDateFrom = (val) => {
    setFilterDateFrom(val);
    updateApiFilter('startDate', val ? val.format('YYYY-MM-DD') : null);
  };
  const handleDateTo = (val) => {
    setFilterDateTo(val);
    updateApiFilter('endDate', val ? val.format('YYYY-MM-DD') : null);
  };
  const handleProjectFilter = (val) => {
    setFilterProject(val);
    updateApiFilter('projectId', val || null);
  };
  const handleCategoryFilter = (val) => {
    setFilterCategory(val);
    updateApiFilter('category', val || null);
  };
  const handleDescFilter = (val) => {
    setFilterDesc(val);
    clearTimeout(descDebounce.current);
    descDebounce.current = setTimeout(() => {
      updateApiFilter('description', val.trim() || null);
    }, 400);
  };

  const hasActiveFilters = filterDateFrom || filterDateTo || filterProject || filterCategory || filterDesc || filterOvertime;

  const clearFilters = () => {
    setFilterDateFrom(null); setFilterDateTo(null);
    setFilterProject(''); setFilterCategory(''); setFilterDesc(''); setFilterOvertime(false);
    setPage(1);
    setApiFilters({});
  };

  const handleOvertimeFilter = (val) => {
    setFilterOvertime(val);
    updateApiFilter('isOvertime', val ? 'true' : null);
  };

  // Description autocomplete — debounced, 3-char minimum
  const handleDescInput = (val) => {
    setForm(f => ({ ...f, description: val }));
    clearTimeout(descInputDebounce.current);
    if (val.length < 3) { setDescOptions([]); setDescLoading(false); return; }
    setDescLoading(true);
    descInputDebounce.current = setTimeout(async () => {
      try {
        const res = await activitiesAPI.autocomplete(val, form.projectId);
        setDescOptions(res.data?.suggestions || []);
      } catch { } finally { setDescLoading(false); }
    }, 350);
  };

  // People autocomplete — debounced, 3-char minimum
  const handlePeopleInput = (val) => {
    setPeopleInput(val);
    clearTimeout(peopleDebounce.current);
    if (val.length < 3) { setPeopleOptions([]); return; }
    peopleDebounce.current = setTimeout(async () => {
      try {
        const res = await activitiesAPI.peopleAutocomplete(val);
        setPeopleOptions(res.data?.suggestions || []);
      } catch { }
    }, 350);
  };

  const handleOpen = (item = null) => {
    setEditItem(item);
    setFullDay(!item); // default to full-day only when creating new
    setForm(item ? {
      projectId: item.projectId || '',
      date: dayjs((item.date || '').slice(0, 10)),
      startTime: item.startTime || '',
      endTime: item.endTime || '',
      description: item.description,
      category: item.category || '',
      milestoneId: item.milestoneId || '',
      overtimeEvidences: item.overtimeEvidences || [],
      relatedPeople: item.relatedPeople?.map(p => (typeof p === 'string' ? p : p.email)).filter(Boolean) || [],
      evidences: item.evidences || []
    } : { ...emptyForm, date: dayjs() });
    setError('');
    setDescOptions([]);
    setDescAppend('');
    setOpenDialog(true);
  };

  const handleFilesUploaded = (files) => {
    setForm(f => ({ ...f, evidences: [...f.evidences, ...files.map(fl => ({ url: fl.url, s3Key: fl.s3Key, fileName: fl.fileName }))] }));
  };

  const handleOvertimeFilesUploaded = (files) => {
    setForm(f => ({ ...f, overtimeEvidences: [...f.overtimeEvidences, ...files.map(fl => ({ url: fl.url, s3Key: fl.s3Key, fileName: fl.fileName }))] }));
  };

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      const isDispute = editItem?.dispute?.status === 'pending';
      const finalDesc = isDispute && descAppend.trim()
        ? `${form.description}\n\n[Adición]: ${descAppend.trim()}`
        : form.description;
      const payload = {
        ...form,
        description: finalDesc,
        date: form.date.format('YYYY-MM-DD'),
        hours: isDispute ? editItem.hours : computedHours,
        isOvertime: isOvertimeDetected,
        relatedPeople: form.relatedPeople.map(p => (typeof p === 'string' ? { email: p } : p)),
        overtimeEvidences: form.overtimeEvidences,
        milestoneId: form.milestoneId || null
      };
      let savedId = editItem?._id;
      if (editItem) {
        await activitiesAPI.update(editItem._id, payload);
      } else if (fullDay) {
        await activitiesAPI.create({ ...payload, startTime: '08:00', endTime: '12:00', hours: 4 });
        const res2 = await activitiesAPI.create({ ...payload, startTime: '14:00', endTime: '18:00', hours: 4 });
        savedId = res2.data?._id;
      } else {
        const res = await activitiesAPI.create(payload);
        savedId = res.data?._id;
      }
      setOpenDialog(false);
      await loadData(page, apiFilters);
      triggerHighlight(savedId);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar actividad');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await activitiesAPI.delete(id);
      setConfirm({ open: false, id: null });
      loadData(page, apiFilters);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const getFileType = (name) => {
    if (!name) return 'other';
    const ext = name.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  const handleOpenEvidence = async (ev) => {
    const s3Key = typeof ev === 'string' ? ev : ev?.s3Key;
    const fileName = typeof ev === 'string' ? ev.split('/').pop() : (ev?.fileName || ev?.s3Key?.split('/').pop() || 'archivo');
    if (!s3Key) return;
    setPreviewLoading(true);
    try {
      const res = await filesAPI.getSignedUrl(s3Key);
      const signedUrl = res.data.url;
      const type = getFileType(fileName);
      if (type === 'other') {
        const a = document.createElement('a');
        a.href = signedUrl;
        a.download = fileName;
        a.target = '_blank';
        a.click();
      } else {
        setPreviewFile({ open: true, signedUrl, fileName, type });
      }
    } catch {
      alert('No se pudo obtener el archivo. Intenta de nuevo.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmitDispute = async () => {
    if (!disputeMsg.trim()) return;
    setDisputeLoading(true);
    try {
      await activitiesAPI.dispute(disputeDialog.actId, { message: disputeMsg });
      setDisputeDialog({ open: false, actId: null });
      setDisputeMsg('');
      loadData(page, apiFilters);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al registrar la refutación');
    } finally { setDisputeLoading(false); }
  };

  // Group activities by date for the grouped table view
  const groupedByDay = useMemo(() => {
    const groups = {};
    activities.forEach(act => {
      // Slice the first 10 chars to avoid UTC→local offset shift (Colombia UTC-5)
      const key = (act.date || '').slice(0, 10);
      if (!groups[key]) groups[key] = [];
      groups[key].push(act);
    });
    return Object.entries(groups); // order preserved from sorted backend response
  }, [activities]);

  if (loading) return <LoadingSpinner />;

  const isDisputeMode = editItem?.dispute?.status === 'pending';

  return (
    <Box>
      <style>{`
        @keyframes rowPulse {
          0%   { background-color: transparent; }
          25%  { background-color: rgba(41, 182, 246, 0.18); }
          50%  { background-color: transparent; }
          75%  { background-color: rgba(41, 182, 246, 0.18); }
          100% { background-color: transparent; }
        }
        .row-highlight { animation: rowPulse 1.4s ease-in-out 2; }
      `}</style>
      <DevBadge id={isTeamLead ? 'page:teamlead-actividades' : 'page:member-actividades'} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {isTeamLead ? 'Actividades del Equipo' : 'Registro de Actividad'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isTeamLead
              ? 'Visualiza las horas registradas por todo el equipo de desarrollo en cada proyecto.'
              : <>Registra las horas trabajadas en cada proyecto. Usa la categoría <strong>BLOQUEO</strong> para reportar tiempo perdido por impedimentos.</>}
          </Typography>
        </Box>
        {!isTeamLead && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Nueva Actividad</Button>
        )}
      </Box>

      {/* Filter bar */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <FilterList sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary">Filtros</Typography>
          {hasActiveFilters && (
            <Button size="small" startIcon={<Clear />} onClick={clearFilters}
              sx={{ ml: 'auto', textTransform: 'none' }}>
              Limpiar
            </Button>
          )}
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker label="Desde" value={filterDateFrom}
              onChange={handleDateFrom}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              maxDate={filterDateTo || dayjs()} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker label="Hasta" value={filterDateTo}
              onChange={handleDateTo}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              minDate={filterDateFrom || undefined}
              maxDate={dayjs()} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField select label="Proyecto" value={filterProject} size="small" fullWidth
              onChange={e => handleProjectFilter(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {projects.map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField select label="Categoría" value={filterCategory} size="small" fullWidth
              onChange={e => handleCategoryFilter(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {categories.map(c => <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Descripción" value={filterDesc} size="small" fullWidth
              onChange={e => handleDescFilter(e.target.value)}
              placeholder="Buscar en descripción..."
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment>,
                endAdornment: filterDesc ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleDescFilter('')}><Clear fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          {isTeamLead && (
            <Grid item xs={12} md="auto">
              <Chip
                label="Solo horas extra"
                clickable
                color={filterOvertime ? 'warning' : 'default'}
                variant={filterOvertime ? 'filled' : 'outlined'}
                onClick={() => handleOvertimeFilter(!filterOvertime)}
                sx={{ height: 40 }}
              />
            </Grid>
          )}
        </Grid>
      </Card>

      {/* History table — grouped by day */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Fecha</TableCell>
                <TableCell>Actividades del día</TableCell>
                <TableCell sx={{ width: 90 }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedByDay.map(([dateKey, dayActs]) => {
                const dayTotal = dayActs.reduce((s, a) => s + (a.hours || 0), 0);
                const hasOvertime = dayActs.some(a => a.isOvertime);
                return (
                  <TableRow key={dateKey} sx={{ verticalAlign: 'top' }}>
                    {/* Date cell */}
                    <TableCell sx={{ pt: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>{dayjs(dateKey).format('DD/MM/YYYY')}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {dayjs(dateKey).format('dddd')}
                      </Typography>
                    </TableCell>

                    {/* Activities cell */}
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      {dayActs.map((act, idx) => {
                        const proj = projects.find(p => p._id === act.projectId);
                        const canEdit = isTeamLead ||
                          (Date.now() - new Date(act.createdAt).getTime()) < 24 * 60 * 60 * 1000 ||
                          act.dispute?.status === 'pending';
                        const isHighlighted = highlightId === act._id;
                        return (
                          <Box key={act._id}
                            className={isHighlighted ? 'row-highlight' : undefined}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1,
                              py: 0.75, px: 1, mx: -1, borderRadius: 1,
                              borderTop: idx > 0 ? '1px solid' : 'none',
                              borderColor: 'divider',
                              bgcolor: act.dispute?.status === 'pending' ? 'rgba(255,152,0,0.06)' : 'transparent',
                            }}>
                            {/* Project */}
                            <Typography variant="body2" sx={{ minWidth: 200, maxWidth: 300, flexShrink: 0 }} noWrap
                              title={proj?.name || act.projectName}>
                              {proj?.name || act.projectName || '—'}
                            </Typography>
                            {/* Description + badges */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Tooltip title={act.description}>
                                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>{act.description}</Typography>
                                </Tooltip>
                                {act.dispute?.status === 'pending' && (
                                  <Tooltip title={`Refutada: ${act.dispute.message}`} arrow>
                                    <GppMaybe sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0, cursor: 'help' }} />
                                  </Tooltip>
                                )}
                                {act.dispute?.status === 'resolved' && (
                                  <Tooltip title="Refutación resuelta" arrow>
                                    <GppMaybe sx={{ fontSize: 16, color: 'success.main', flexShrink: 0, cursor: 'help' }} />
                                  </Tooltip>
                                )}
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                {isTeamLead && act.userEmail && (
                                  <Typography variant="caption" color="text.secondary">{act.userEmail}</Typography>
                                )}
                              </Box>
                            </Box>
                            {/* Category + Overtime */}
                            {act.isOvertime && (
                              <Tooltip title={`Horas extra: +${act.overtimeHours ?? '?'}h`} arrow>
                                <Chip label="Extra" size="small" color="warning" sx={{ height: 20, fontSize: 11, flexShrink: 0 }} />
                              </Tooltip>
                            )}
                            {act.category && (
                              <Chip label={act.category} size="small" sx={{ height: 20, fontSize: 11, flexShrink: 0,
                                bgcolor: (categories.find(c => c.name === act.category)?.color || '') + '20' }} />
                            )}
                            {/* Hours */}
                            <Chip label={`${act.hours}h`} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                              <Tooltip title="Ver detalle" arrow>
                                <IconButton size="small"
                                  sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }}
                                  onClick={() => setViewItem(act)}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!isTeamLead && (
                                <>
                                  <Tooltip title={canEdit ? (act.dispute?.status === 'pending' ? 'Responder refutación' : 'Editar') : 'Solo editable dentro de las primeras 24h'} arrow>
                                    <span>
                                      <IconButton size="small"
                                        sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }}
                                        onClick={() => handleOpen(act)} disabled={!canEdit}>
                                        {act.dispute?.status === 'pending' ? <Reply fontSize="small" /> : <Edit fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={canEdit ? 'Eliminar' : 'Solo eliminable dentro de las primeras 24h'} arrow>
                                    <span>
                                      <IconButton size="small" color="error" disabled={!canEdit}
                                        sx={{ borderRadius: '5px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }}
                                        onClick={() => setConfirm({ open: true, id: act._id })}>
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                              {canDispute && (
                                <Tooltip title={act.dispute?.status === 'pending' ? 'Refutación pendiente' : 'Refutar actividad'} arrow>
                                  <span>
                                    <IconButton size="small"
                                      color={act.dispute?.status === 'pending' ? 'error' : 'default'}
                                      disabled={act.dispute?.status === 'pending'}
                                      sx={{ borderRadius: '5px', bgcolor: act.dispute?.status === 'pending' ? 'rgba(211,47,47,0.08)' : 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: act.dispute?.status === 'pending' ? 'rgba(211,47,47,0.18)' : 'rgba(0,0,0,0.13)' } }}
                                      onClick={() => { setDisputeDialog({ open: true, actId: act._id }); setDisputeMsg(''); }}>
                                      <GppMaybe fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </TableCell>

                    {/* Daily total */}
                    <TableCell align="right" sx={{ pt: 1.5 }}>
                      <Chip
                        label={`${dayTotal % 1 === 0 ? dayTotal : dayTotal.toFixed(2)}h`}
                        size="small"
                        color={hasOvertime ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {groupedByDay.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      {isTeamLead
                        ? 'No hay actividades registradas para los filtros seleccionados.'
                        : '¡No hay actividades registradas aún. Registra tu primera actividad!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Activity form dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isDisputeMode && <Reply color="warning" />}
          {editItem ? (isDisputeMode ? 'Responder Refutación' : 'Editar Actividad') : 'Nueva Actividad'}
          <DevBadge id="modal:actividad" inline />
        </DialogTitle>
        <DialogContent>
          {editItem?.dispute?.status === 'pending' && (
            <Alert severity="warning" icon={<GppMaybe />} sx={{ mb: 2 }}>
              <strong>Actividad refutada por tu líder:</strong> {editItem.dispute.message}
              <Typography variant="caption" display="block" mt={0.5}>
                Complementa con más detalle o evidencias y guarda para marcarla como resuelta.
              </Typography>
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Proyecto *" value={form.projectId}
                onChange={e => setForm(f => ({ ...f, projectId: e.target.value }))} fullWidth
                disabled={isDisputeMode}>
                {projects.map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker label="Fecha *" value={form.date}
                onChange={val => setForm(f => ({ ...f, date: val }))}
                slotProps={{ textField: { fullWidth: true } }}
                maxDate={dayjs()} disabled={isDisputeMode} />
            </Grid>
            {isDisputeMode ? (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1.5}
                  sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, px: 2, height: 56 }}>
                  <Schedule sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Horas registradas</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {editItem?.hours || 0}h{editItem?.startTime && editItem?.endTime ? ` · ${editItem.startTime}–${editItem.endTime}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ) : (
              <>
                {/* Full-day toggle — only for new activities */}
                {!editItem && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={fullDay}
                          onChange={e => {
                            setFullDay(e.target.checked);
                            if (e.target.checked) {
                              setForm(f => ({ ...f, startTime: '', endTime: '' }));
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>Jornada completa</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Se crearán dos actividades: 08:00–12:00 y 14:00–18:00
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                )}

                {/* Time pickers — hidden when fullDay is active for new activities */}
                {(!fullDay || editItem) && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Hora de inicio *" type="time" value={form.startTime}
                        onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                        fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Hora de fin *" type="time" value={form.endTime}
                        onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                        fullWidth InputLabelProps={{ shrink: true }}
                        error={!!(form.startTime && form.endTime && computedHours <= 0)}
                        helperText={form.startTime && form.endTime && computedHours <= 0 ? 'Debe ser posterior al inicio' : ''} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Duración calculada"
                        value={computedHours > 0 ? `${computedHours} horas` : '—'}
                        fullWidth InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
                        helperText="Se calcula automáticamente" />
                    </Grid>
                  </>
                )}
                {form.projectId && dateStr && (
                  <Grid item xs={12}>
                    <Box sx={{ px: 2, py: 1, borderRadius: 1, border: '1px solid',
                      bgcolor: isOvertimeDetected ? 'rgba(237,108,2,0.06)' : 'grey.50',
                      borderColor: isOvertimeDetected ? 'warning.main' : 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        {dailyHoursLoading ? 'Calculando horas del día...' : (() => {
                          const addHours = (!editItem && fullDay) ? 8 : computedHours;
                          const scheduleEndTime = selectedProject?.workSchedule?.endTime || '18:00';
                          return (
                            <>Horas registradas hoy para este proyecto: <strong>{dailyHoursLogged.toFixed(2)}h</strong>
                              {addHours > 0 && <> · Esta actividad: <strong style={{ color: isOvertimeDetected ? '#ed6c02' : 'inherit' }}>{addHours}h</strong></>}
                              {' · '}Fin de jornada: <strong>{scheduleEndTime}</strong>
                            </>
                          );
                        })()}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {isOvertimeDetected && (
                  <Grid item xs={12}>
                    <Alert severity="warning" icon={<ReportProblemOutlined />}>
                      {(() => {
                        // Holiday takes priority in the message
                        if (isHolidayDetected) {
                          return (
                            <>
                              <strong>Día festivo: {isHolidayDetected.name}</strong><br />
                              El día seleccionado es un festivo del cliente. Todo el tiempo registrado se considera horas extra completas, independientemente del horario.<br />
                              Debes adjuntar evidencia de aprobación para poder guardar.
                            </>
                          );
                        }
                        const ws = selectedProject?.workSchedule;
                        const scheduleEndTime = ws?.endTime || '18:00';
                        const workDays = ws?.workDays?.length ? ws.workDays : [1, 2, 3, 4, 5];
                        const jsDay = form.date?.toDate?.()?.getDay?.() ?? -1;
                        const isoDay = jsDay === 0 ? 7 : jsDay;
                        const isNonWorkingDay = !workDays.includes(isoDay);
                        if (isNonWorkingDay) {
                          return (
                            <>
                              <strong>Día no laboral</strong><br />
                              El día seleccionado no es un día laboral para este proyecto. Todo el tiempo registrado se considera horas extra.<br />
                              Debes adjuntar evidencia de aprobación para poder guardar.
                            </>
                          );
                        }
                        const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                        const extraHours = parseFloat(Math.max(0, (toMin(form.endTime) - toMin(scheduleEndTime)) / 60).toFixed(2));
                        return (
                          <>
                            <strong>Horas extra detectadas: +{extraHours}h</strong><br />
                            La hora de fin (<strong>{form.endTime}</strong>) supera el horario de jornada (<strong>{scheduleEndTime}</strong>).<br />
                            Debes adjuntar evidencia de aprobación para poder guardar.
                          </>
                        );
                      })()}
                    </Alert>
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12}>
              {isDisputeMode ? (
                <>
                  <TextField
                    label="Descripción original (solo lectura)"
                    value={form.description}
                    multiline rows={2} fullWidth disabled
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.65)' } }}
                  />
                  <TextField
                    label="Información adicional"
                    value={descAppend}
                    onChange={e => setDescAppend(e.target.value)}
                    multiline rows={2} fullWidth
                    placeholder="Añade detalles o complementa la descripción original..."
                    sx={{ mt: 1.5 }}
                    helperText="Solo puedes añadir información. El texto original no puede modificarse ni eliminarse."
                  />
                </>
              ) : (
                <Autocomplete
                  freeSolo
                  options={descOptions}
                  loading={descLoading}
                  inputValue={form.description}
                  onInputChange={(_, val) => handleDescInput(val)}
                  onChange={(_, val) => { if (val) setForm(f => ({ ...f, description: val })); }}
                  renderInput={(params) => (
                    <TextField {...params} label="Descripción de la actividad *" multiline rows={2} fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (<>{descLoading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>)
                      }} />
                  )}
                  noOptionsText="Sin sugerencias"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Categoría" value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))} fullWidth
                disabled={isDisputeMode}>
                <MenuItem value="">Sin categoría</MenuItem>
                {categories.map(c => <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            {milestones.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField select label="Hito" value={form.milestoneId}
                  onChange={e => setForm(f => ({ ...f, milestoneId: e.target.value }))} fullWidth>
                  <MenuItem value="">(Sin hito)</MenuItem>
                  {milestones.map(ms => <MenuItem key={ms._id} value={ms._id}>{ms.name}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            {form.category === 'BLOQUEO' && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<BlockOutlined />}
                  sx={{ borderRadius: 1 }}>
                  <strong>Registro de bloqueo:</strong> Estás registrando una pérdida de tiempo por bloqueo.
                  Describe en detalle qué te está impidiendo avanzar y qué se requiere para resolverlo.
                  Las horas registradas aquí quedarán asociadas al proyecto como tiempo perdido.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={peopleOptions}
                value={form.relatedPeople}
                inputValue={peopleInput}
                onInputChange={(_, val, reason) => { if (reason === 'reset') { setPeopleInput(''); } else { handlePeopleInput(val); } }}
                onChange={(_, val) => {
                  const normalized = val
                    .map(v => (typeof v === 'string' ? v.trim() : v))
                    .filter(v => EMAIL_REGEX.test(typeof v === 'string' ? v : v.email || ''));
                  setForm(f => ({ ...f, relatedPeople: normalized }));
                }}
                filterOptions={(options, params) => {
                  const input = params.inputValue.trim();
                  const filtered = options.filter(o => o.toLowerCase().includes(input.toLowerCase()));
                  if (input && EMAIL_REGEX.test(input) && !options.includes(input)) {
                    filtered.push(input);
                  }
                  return filtered;
                }}
                getOptionLabel={opt => (typeof opt === 'string' ? opt : opt.email || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Personas relacionadas"
                    placeholder={form.relatedPeople.length === 0 ? 'correo@ejemplo.com' : ''}
                    fullWidth
                    helperText={
                      peopleInput && !EMAIL_REGEX.test(peopleInput.trim())
                        ? 'Escribe un correo electrónico válido y presiona Enter para añadirlo'
                        : 'Opcional · Ingresa correos electrónicos y presiona Enter'
                    }
                  />
                )}
                noOptionsText="Sin sugerencias"
                disabled={isDisputeMode}
              />
            </Grid>

            {/* Evidencias de la actividad */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" mb={1}>
                Evidencias de la actividad{' '}
                {isDisputeMode ? '(solo lectura — puedes añadir nuevos archivos)' : '(opcional)'}
              </Typography>
              {isDisputeMode && editItem?.evidences?.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                  {editItem.evidences.filter(Boolean).map((ev, i) => {
                    const name = typeof ev === 'string' ? ev.split('/').pop() : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                    return (
                      <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)} size="small" variant="outlined"
                        onClick={() => handleOpenEvidence(ev)}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              )}
              {!isDisputeMode && form.evidences.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                  {form.evidences.filter(Boolean).map((ev, i) => {
                    const name = typeof ev === 'string' ? ev.split('/').pop() : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                    return (
                      <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)} size="small" variant="outlined"
                        onDelete={() => setForm(f => ({ ...f, evidences: f.evidences.filter((_, idx) => idx !== i) }))}
                        sx={{ cursor: 'default' }}
                      />
                    );
                  })}
                </Box>
              )}
              <FileUpload onFilesUploaded={handleFilesUploaded} maxFiles={10} />
            </Grid>

            {/* Evidencias de aprobación de horas extra — solo en registro nuevo, no en respuesta a refutación */}
            {!isDisputeMode && isOvertimeDetected && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" mb={0.5} color="warning.dark">
                  Evidencias de aprobación de horas extra <span style={{ color: '#ed6c02' }}>*</span>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Adjunta los comprobantes o aprobaciones que justifiquen el registro de horas extra.
                </Typography>
                {isDisputeMode && editItem?.overtimeEvidences?.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                    {editItem.overtimeEvidences.filter(Boolean).map((ev, i) => {
                      const name = typeof ev === 'string' ? ev.split('/').pop() : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                      return (
                        <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)} size="small"
                          color="warning" variant="outlined"
                          onClick={() => handleOpenEvidence(ev)}
                          sx={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Box>
                )}
                {!isDisputeMode && form.overtimeEvidences.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                    {form.overtimeEvidences.filter(Boolean).map((ev, i) => {
                      const name = typeof ev === 'string' ? ev.split('/').pop() : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                      return (
                        <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)} size="small"
                          color="warning" variant="outlined"
                          onDelete={() => setForm(f => ({ ...f, overtimeEvidences: f.overtimeEvidences.filter((_, idx) => idx !== i) }))}
                          sx={{ cursor: 'default' }}
                        />
                      );
                    })}
                  </Box>
                )}
                <FileUpload onFilesUploaded={handleOvertimeFilesUploaded} maxFiles={10} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={saving || !form.projectId || !form.description || !form.date ||
              (!isDisputeMode && !(!editItem && fullDay) && (!form.startTime || !form.endTime || computedHours <= 0)) ||
              (!isDisputeMode && isOvertimeDetected && form.overtimeEvidences.length === 0)}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File preview dialog */}
      <Dialog open={previewFile.open} onClose={() => setPreviewFile(p => ({ ...p, open: false }))} maxWidth="md" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" noWrap sx={{ flex: 1, mr: 1 }}>{previewFile.fileName}</Typography>
          <IconButton size="small" onClick={() => setPreviewFile(p => ({ ...p, open: false }))}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: previewFile.type === 'image' ? 2 : 0, display: 'flex', justifyContent: 'center', minHeight: 300 }}>
          {previewFile.type === 'image' && (
            <img src={previewFile.signedUrl} alt={previewFile.fileName}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          )}
          {previewFile.type === 'pdf' && (
            <iframe src={previewFile.signedUrl} title={previewFile.fileName}
              style={{ width: '100%', height: '70vh', border: 'none' }} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button component="a" href={previewFile.signedUrl} download={previewFile.fileName}
            target="_blank" startIcon={<Download />} variant="outlined">
            Descargar
          </Button>
          <Button onClick={() => setPreviewFile(p => ({ ...p, open: false }))}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* View detail dialog — read-only */}
      <Dialog open={Boolean(viewItem)} onClose={() => { triggerHighlight(viewItem?._id); setViewItem(null); }} maxWidth="sm" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Visibility sx={{ color: 'text.secondary', fontSize: 20 }} />
            Detalle de Actividad
          </Box>
          {viewItem?.dispute?.status === 'pending' && (
            <Chip icon={<GppMaybe />} label="Refutada" size="small" color="warning" />
          )}
          {viewItem?.dispute?.status === 'resolved' && (
            <Chip icon={<GppMaybe />} label="Refutación resuelta" size="small" color="success" />
          )}
        </DialogTitle>
        <DialogContent>
          {viewItem && (
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Proyecto</Typography>
                <Typography variant="body2" fontWeight={500}>{projects.find(p => p._id === viewItem.projectId)?.name || viewItem.projectName || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Fecha</Typography>
                <Typography variant="body2" fontWeight={500}>{dayjs((viewItem.date || '').slice(0, 10)).format('DD/MM/YYYY')}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Horas</Typography>
                <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mt={0.25}>
                  {viewItem.isOvertime ? (
                    <>
                      <Chip
                        label={`${parseFloat((viewItem.hours - (viewItem.overtimeHours || 0)).toFixed(2))}h normales`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`+${viewItem.overtimeHours ?? '?'}h extra`}
                        size="small"
                        color="warning"
                        icon={<ReportProblemOutlined />}
                      />
                    </>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>{viewItem.hours}h</Typography>
                  )}
                </Box>
              </Grid>
              {viewItem.startTime && viewItem.endTime && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Horario de la actividad</Typography>
                  <Typography variant="body2" fontWeight={500}>{viewItem.startTime} – {viewItem.endTime}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Descripción</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{viewItem.description}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Categoría</Typography>
                <Typography variant="body2" fontWeight={500}>{viewItem.category || '—'}</Typography>
              </Grid>
              {isTeamLead && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Registrado por</Typography>
                  <Typography variant="body2" fontWeight={500}>{viewItem.userEmail || '—'}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Registrado el</Typography>
                <Typography variant="body2" fontWeight={500}>{dayjs(viewItem.createdAt).format('DD/MM/YYYY HH:mm')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Personas relacionadas</Typography>
                {viewItem.relatedPeople?.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {viewItem.relatedPeople.map((p, i) => (
                      <Chip key={i} label={typeof p === 'string' ? p : p.email || p} size="small" variant="outlined" />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" fontWeight={500}>—</Typography>
                )}
              </Grid>
              {viewItem.evidences?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">Evidencias de la actividad ({viewItem.evidences.length})</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                    {viewItem.evidences.map((ev, i) => {
                      const name = !ev ? `Archivo ${i + 1}` : typeof ev === 'string' ? ev.split('/').pop() : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                      return (
                        <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)} size="small" variant="outlined"
                          onClick={() => handleOpenEvidence(ev)}
                          sx={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              )}
              {viewItem.overtimeEvidences?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="warning.dark">Evidencias de aprobación de horas extra ({viewItem.overtimeEvidences.length})</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                    {viewItem.overtimeEvidences.map((ev, i) => {
                      const name = !ev ? `Archivo ${i + 1}` : typeof ev === 'string' ? ev.split('/').pop() : (ev.fileName || ev.s3Key?.split('/').pop() || `Archivo ${i + 1}`);
                      return (
                        <Chip key={i} icon={<AttachFile />} label={name?.substring(0, 35)} size="small"
                          color="warning" variant="outlined"
                          onClick={() => handleOpenEvidence(ev)}
                          sx={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              )}
              {viewItem.dispute?.status !== 'none' && viewItem.dispute?.message && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Alert severity={viewItem.dispute.status === 'pending' ? 'warning' : 'success'} icon={<GppMaybe />}>
                    <strong>{viewItem.dispute.status === 'pending' ? 'Refutación pendiente:' : 'Refutación resuelta:'}</strong> {viewItem.dispute.message}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { triggerHighlight(viewItem?._id); setViewItem(null); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dispute dialog — teamlead only */}
      <Dialog open={disputeDialog.open} onClose={() => setDisputeDialog({ open: false, actId: null })}
        maxWidth="sm" fullWidth TransitionComponent={SlideTransition}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportProblemOutlined color="warning" /> Refutar Actividad
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Indica al miembro del equipo qué debe complementar (descripción adicional o evidencias).
          </Typography>
          <TextField
            label="Motivo de refutación *" multiline rows={3} fullWidth autoFocus
            value={disputeMsg} onChange={e => setDisputeMsg(e.target.value)}
            placeholder="Ej: Falta evidencia del progreso realizado en el sprint..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisputeDialog({ open: false, actId: null })}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleSubmitDispute}
            disabled={disputeLoading || !disputeMsg.trim()}>
            {disputeLoading ? 'Enviando...' : 'Refutar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open} title="Eliminar Actividad"
        message="¿Eliminar este registro de actividad? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        onConfirm={() => handleDelete(confirm.id)}
        onCancel={() => setConfirm({ open: false, id: null })}
      />
    </Box>
  );
}
